
(() => {
  function init() {
    const configElement = document.getElementById("axiom-shopify-config");
    if (!configElement || configElement.dataset.initialized) return;
    configElement.dataset.initialized = "true";
    const config = JSON.parse(configElement.textContent);
    const lifecycle = new AbortController();
    document.addEventListener("shopify:section:unload", event => {
      if (event.target.contains(configElement)) {
        lifecycle.abort();
        document.getElementById("axiom-mobile-menu")?.remove();
      }
    }, {signal: lifecycle.signal});
    const root = (config.root || "/").replace(/\/?$/, "/");
    const singlePriceCents = Number(config.singlePrice);
    const offerTotals = config.offerTotals || {};
    const subscriptionTotalCents = Number(config.subscriptionTotal);
    const subscriptionQuantity = 1;
    const sellingPlanId = String(config.sellingPlan ?? "").trim();
    const hasNumericSellingPlan = /^\d+$/.test(sellingPlanId) && Number(sellingPlanId) > 0;
    const money = cents => new Intl.NumberFormat("en-US", {
      style: "currency", currency: "USD"
    }).format(cents / 100);
    const text = element => element.textContent.replace(/\s+/g, " ").trim();
    const allButtons = [...document.querySelectorAll("button")];
    const cards = [...document.querySelectorAll("button.offer-card")];
    const onceButtons = allButtons.filter(button => text(button).startsWith("One-time purchase"));
    const subButtons = allButtons.filter(button => text(button).startsWith("Subscribe"));
    const purchaseButtons = allButtons.filter(button => text(button).startsWith("Lock in now"));
    let count = 2;
    let subscription = false;
    let requestVersion = 0;
    let busy = false;
    let readyOffer = null;

    // Preserve the original selected-card classes.
    const selectedCard = cards.find(card => card.getAttribute("aria-pressed") === "true");
    const unselectedCard = cards.find(card => card.getAttribute("aria-pressed") !== "true");
    const selectedClasses = selectedCard && unselectedCard
      ? [...selectedCard.classList].filter(name => !unselectedCard.classList.contains(name))
      : [];
    cards.forEach(card => {
      card.type = "button";
      card.dataset.axiomCount = text(card).match(/^[123]/)[0];
    });

    const statusElements = purchaseButtons.map(button => {
      button.type = "button";
      const status = document.createElement("p");
      status.className = "axiom-purchase-status";
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      button.insertAdjacentElement("afterend", status);
      return status;
    });
    const status = message => statusElements.forEach(element => {
      element.textContent = message;
    });

    function state() {
      return {
        count,
        quantity: subscription ? subscriptionQuantity : count,
        total: subscription ? subscriptionTotalCents : Number(offerTotals[count]),
        handle: config.product
      };
    }

    async function resolveOffer() {
      const current = state();
      const isSubscription = subscription;
      if (config.currency !== "USD" || !current.handle) return null;
      if (!isSubscription && !config.quantityPricingVerified) return null;
      if (isSubscription && (!config.subscription30Day4999Verified || !hasNumericSellingPlan)) return null;
      const response = await fetch(
        root + "products/" + encodeURIComponent(current.handle) + ".js",
        {headers: {"Accept": "application/json"}, cache: "no-store"}
      );
      if (!response.ok) throw new Error("Unable to check this option.");
      const product = await response.json();
      const matches = (product.variants || []).filter(variant => {
        if (!variant.available || Number(variant.price) !== singlePriceCents) return false;
        if (!isSubscription) return !product.requires_selling_plan && !variant.requires_selling_plan;
        return (variant.selling_plan_allocations || []).some(allocation =>
          String(allocation.selling_plan_id) === sellingPlanId
        );
      });
      // Never guess between multiple qualifying variants.
      if (matches.length !== 1) return null;
      return {
        id: matches[0].id,
        quantity: current.quantity,
        ...(isSubscription ? {selling_plan: sellingPlanId} : {})
      };
    }

    function display() {
      const current = state();
      cards.forEach(card => {
        const selected = Number(card.dataset.axiomCount) === count;
        card.setAttribute("aria-pressed", String(selected));
        selectedClasses.forEach(name => card.classList.toggle(name, selected));
        if (Number(card.dataset.axiomCount) === 1) {
          const price = subscription ? subscriptionTotalCents : singlePriceCents;
          const priceLabel = card.querySelector(".offer-price strong");
          const perBottle = card.querySelectorAll(".offer-copy small")[1];
          if (priceLabel) priceLabel.textContent = money(price);
          if (perBottle) perBottle.textContent = money(price) + " / bottle";
        }
      });
      document.querySelectorAll(".offer-selector .selector-label small").forEach(label => {
        label.textContent = subscription ? "Subscription ships one bottle" : "One-time prices below";
      });
      onceButtons.forEach(button => {
        button.type = "button";
        button.setAttribute("aria-pressed", String(!subscription));
        button.classList.toggle("selected", !subscription);
      });
      subButtons.forEach(button => {
        button.type = "button";
        button.setAttribute("aria-pressed", String(subscription));
        button.classList.toggle("selected", subscription);
      });
      document.querySelectorAll(".buy-box-heading h2").forEach(heading => {
        heading.textContent = count + (count === 1 ? " Bottle" : " Bottles");
      });
      document.querySelectorAll(".buy-price strong").forEach(price => {
        price.textContent = money(current.total);
      });
      document.querySelectorAll(".buy-price del").forEach(price => {
        price.textContent = money(singlePriceCents * current.quantity);
        price.hidden = current.total >= singlePriceCents * current.quantity;
      });
      document.querySelectorAll(".subscription-note").forEach(note => {
        note.textContent = subscription
          ? `Subscription selected: 1 bottle every 30 days, charged ${money(subscriptionTotalCents)} every 30 days plus $4.99 shipping.`
          : `Optional subscription: 1 bottle every 30 days, charged ${money(subscriptionTotalCents)} every 30 days plus $4.99 shipping. No subscription is selected.`;
      });
      document.querySelectorAll(".mobile-buy-bar small").forEach(label => {
        label.textContent = `${current.quantity} ${current.quantity === 1 ? 'Bottle' : 'Bottles'}${subscription ? ' · Subscription' : ''}`;
      });
      document.querySelectorAll(".mobile-buy-bar strong").forEach(price => {
        price.textContent = money(current.total);
      });
      document.querySelectorAll(".mobile-buy-bar a").forEach(link => {
        link.textContent = "Lock in";
        link.setAttribute("aria-label", `Review ${current.quantity} ${current.quantity === 1 ? 'bottle' : 'bottles'} for ${money(current.total)}${subscription ? ' every 30 days, plus shipping' : ', one-time purchase'}`);
      });
      document.querySelectorAll(".axiom-selected-shipping").forEach(label => {
        label.textContent = current.quantity === 1 ? "$4.99 shipping" : "Free shipping";
      });
      document.querySelectorAll(".axiom-subscription-help").forEach(note => {
        note.hidden = !subscription;
      });
      purchaseButtons.forEach(button => {
        button.disabled = busy || !readyOffer;
        button.textContent = busy ? "Adding…" : "Lock in now · " + money(current.total);
      });
    }

    async function refresh() {
      const version = ++requestVersion;
      readyOffer = null;
      status("");
      display();
      try {
        const offer = await resolveOffer();
        if (version !== requestVersion) return;
        readyOffer = offer;
        if (!offer) {
          if (config.designPreview) status("Design preview — checkout is available on the Shopify store after setup.");
          else if (!config.product || (!subscription && !config.quantityPricingVerified) || (subscription && (!config.subscription30Day4999Verified || !hasNumericSellingPlan))) status("This offer is being updated. Contact contact@axiomnutrition.com for help.");
          else status("This option is currently unavailable.");
        }
      } catch (error) {
        if (version !== requestVersion) return;
        status("Unable to check availability. Please refresh and try again.");
      }
      display();
    }

    cards.forEach(card => card.addEventListener("click", () => {
      if (busy) return;
      count = Number(card.dataset.axiomCount);
      if (count !== 1) subscription = false;
      refresh();
    }));
    onceButtons.forEach(button => button.addEventListener("click", () => {
      if (busy) return;
      subscription = false;
      refresh();
    }));
    subButtons.forEach(button => button.addEventListener("click", () => {
      if (busy) return;
      subscription = true;
      count = 1;
      refresh();
    }));

    async function purchase() {
      if (busy || !readyOffer) return;
      busy = true;
      status("");
      display();
      try {
        // Recheck the actual Shopify price immediately before adding.
        const offer = await resolveOffer();
        if (!offer) throw new Error("This option is currently unavailable.");
        const getCart = async () => {
          const response = await fetch(root + "cart.js", {
            headers: {"Accept": "application/json"}, cache: "no-store"
          });
          if (!response.ok) throw new Error("Unable to verify the subscription total.");
          return response.json();
        };
        const subscriptionLine = (cart) => (cart.items || []).find(item =>
          Number(item.variant_id) === Number(offer.id) &&
          String(item.selling_plan_allocation?.selling_plan?.id ?? "") === sellingPlanId
        );
        const changeLine = async (key, quantity) => {
          const response = await fetch(root + "cart/change.js", {
            method: "POST",
            headers: {"Content-Type": "application/json", "Accept": "application/json"},
            body: JSON.stringify({id: key, quantity})
          });
          if (!response.ok) throw new Error("Unable to update the subscription in your cart.");
        };
        // Reusing an existing subscription line avoids silently increasing the bottle count.
        const existing = subscription ? subscriptionLine(await getCart()) : null;
        if (existing && Number(existing.quantity) !== subscriptionQuantity) {
          await changeLine(existing.key, subscriptionQuantity);
        }
        if (!existing) {
          const response = await fetch(root + "cart/add.js", {
            method: "POST",
            headers: {"Content-Type": "application/json", "Accept": "application/json"},
            body: JSON.stringify({items: [offer]})
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || "Unable to add this item.");
        }
        if (subscription) {
          const cart = await getCart();
          const line = subscriptionLine(cart);
          const lineTotal = Number(line?.final_line_price ?? line?.line_price);
          if (!line || Number(line.quantity) !== subscriptionQuantity || lineTotal !== subscriptionTotalCents) {
            let restored = false;
            if (line?.key && (!existing || Number(existing.quantity) !== subscriptionQuantity)) {
              try {
                await changeLine(line.key, existing ? Number(existing.quantity) : 0);
                restored = true;
              } catch (_) { /* Keep the error visible if cleanup fails. */ }
            }
            const returnedTotal = Number.isFinite(lineTotal) ? money(lineTotal) : "an incorrect amount";
            const cleanupMessage = restored ? "your previous cart was restored." : "please check the cart before checkout.";
            throw new Error(`The subscription total did not match ${money(subscriptionTotalCents)} every 30 days. Shopify returned ${returnedTotal}; ${cleanupMessage}`);
          }
        }
        window.location.assign(root + "cart");
      } catch (error) {
        status(error.message || "Unable to add this item. Please try again.");
        busy = false;
        display();
      }
    }
    purchaseButtons.forEach(button => button.addEventListener("click", purchase));

    // Restore keyboard-accessible FAQ disclosure controls.
    document.querySelectorAll('[data-slot="accordion-trigger"]').forEach(button => {
      const panel = document.getElementById(button.getAttribute("aria-controls"))
        || [...document.querySelectorAll('[data-slot="accordion-content"]')]
          .find(element => element.getAttribute("aria-labelledby") === button.id);
      if (!panel) return;
      button.setAttribute("aria-controls", panel.id);
      button.type = "button";
      button.setAttribute("aria-expanded", "false");
      button.dataset.state = "closed";
      panel.hidden = true;
      panel.dataset.state = "closed";
      panel.setAttribute("role", "region");
      panel.setAttribute("aria-labelledby", button.id);
      button.addEventListener("click", () => {
        const open = button.getAttribute("aria-expanded") !== "true";
        button.setAttribute("aria-expanded", String(open));
        button.dataset.state = open ? "open" : "closed";
        panel.dataset.state = open ? "open" : "closed";
        panel.hidden = !open;
      });
    });

    // A nonmodal navigation menu, retaining the page's section destinations.
    const menuButton = allButtons.find(button => text(button) === "Menu");
    if (menuButton) {
      const menu = document.createElement("nav");
      menu.id = "axiom-mobile-menu";
      menu.className = "axiom-mobile-menu";
      menu.setAttribute("aria-label", "Main navigation");
      menu.hidden = true;
      [
        ["Overview", "#top"],
        ["Why Axiom", "#why"],
        ["Formula", "#formula"],
        ["Reviews", "#reviews"],
        ["FAQ", "#faq"],
        ["Lock In", "#first-offer"],
        ["Cart", root + "cart"]
      ].forEach(([label, href]) => {
        const link = document.createElement("a");
        link.textContent = label;
        link.href = href;
        menu.append(link);
      });
      document.body.append(menu);
      menuButton.type = "button";
      menuButton.setAttribute("aria-controls", menu.id);
      menuButton.setAttribute("aria-expanded", "false");
      function closeMenu(restoreFocus = false) {
        menu.hidden = true;
        menuButton.setAttribute("aria-expanded", "false");
        if (restoreFocus) menuButton.focus();
      }
      menuButton.addEventListener("click", () => {
        const open = menu.hidden;
        menu.hidden = !open;
        menuButton.setAttribute("aria-expanded", String(open));
        if (open) {
          menu.style.top = Math.max(12, menuButton.getBoundingClientRect().bottom + 12) + "px";
          menu.querySelector("a").focus();
        }
      });
      menu.addEventListener("click", event => {
        if (event.target.closest("a")) closeMenu();
      });
      document.addEventListener("click", event => {
        if (!menu.hidden && !menu.contains(event.target) && !menuButton.contains(event.target)) closeMenu();
      }, {signal: lifecycle.signal});
      document.addEventListener("keydown", event => {
        if (event.key === "Escape" && !menu.hidden) closeMenu(true);
      }, {signal: lifecycle.signal});
    }
    refresh();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", () => {
    init();
  });
})();
